import {
  createSargasso,
  type ProbeResult,
  type SargassoController,
  type SargassoOptions,
} from "@sargasso/core";
import {
  attributeToOption,
  makeEvent,
  OBSERVED_CANVAS_ATTRIBUTES,
  type ResolvedCanvasOptions,
  SARGASSO_CHANGE_EVENT,
  type SargassoChangeDetail,
} from "./bridge";
import STYLE from "./sargasso-canvas.css?inline";

/**
 * `<sargasso-canvas>` — a drop-in custom element that renders the Sargasso
 * contour visualization. Configure it with attributes (`extent`, `speed`,
 * `waves` as JSON, …) or JS properties, and read wave height at a point with
 * {@link probe}.
 */
export class SargassoCanvasElement extends HTMLElement {
  static get observedAttributes(): readonly string[] {
    return OBSERVED_CANVAS_ATTRIBUTES;
  }

  private readonly canvas: HTMLCanvasElement;
  private controller: SargassoController | null = null;
  private pending: Partial<SargassoOptions> = {};

  constructor() {
    super();
    const root = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = STYLE;
    this.canvas = document.createElement("canvas");
    root.append(style, this.canvas);
  }

  connectedCallback(): void {
    if (this.controller) return;
    this.controller = createSargasso(this.canvas, { ...this.pending });
    this.emitChange();
  }

  disconnectedCallback(): void {
    this.controller?.destroy();
    this.controller = null;
  }

  attributeChangedCallback(name: string, _old: string | null, value: string | null): void {
    const patch = attributeToOption(name, value);
    if (patch) this.setOptions(patch);
  }

  /** Merge an options patch, updating the live controller and notifying listeners. */
  setOptions(patch: Partial<SargassoOptions>): void {
    this.pending = { ...this.pending, ...patch };
    if (this.controller) {
      this.controller.setOptions(patch);
      this.emitChange();
    }
  }

  /** The live controller, or null before the element is connected. */
  get sargasso(): SargassoController | null {
    return this.controller;
  }

  /** Whether a WebGL2 context was acquired. */
  get supported(): boolean {
    return this.controller?.supported ?? false;
  }

  /** Current, fully-resolved options exposed to controls. */
  get options(): ResolvedCanvasOptions {
    return this.readOptions();
  }

  get waves(): SargassoOptions["waves"] {
    return this.readOptions().waves;
  }
  set waves(value: SargassoOptions["waves"]) {
    this.setOptions({ waves: value });
  }

  get extent(): number {
    return this.readOptions().extent;
  }
  set extent(value: number) {
    this.setOptions({ extent: value });
  }

  get speed(): number {
    return this.readOptions().speed;
  }
  set speed(value: number) {
    this.setOptions({ speed: value });
  }

  get contour(): ResolvedCanvasOptions["contour"] {
    return this.readOptions().contour;
  }
  set contour(value: ResolvedCanvasOptions["contour"]) {
    this.setOptions({ contour: value });
  }

  /** Sample the wave field at a canvas-relative CSS pixel. */
  probe(x: number, y: number): ProbeResult | null {
    return this.controller?.probe({ x, y }) ?? null;
  }

  private readOptions(): ResolvedCanvasOptions {
    if (this.controller) {
      const o = this.controller.getOptions();
      return {
        waves: o.waves,
        extent: o.extent,
        speed: o.speed,
        steepness: o.steepness,
        contour: o.contour,
      };
    }
    // Not yet connected: report pending config, falling back to sensible defaults.
    return {
      waves: this.pending.waves ?? [],
      extent: this.pending.extent ?? 20,
      speed: this.pending.speed ?? 1,
      steepness: this.pending.steepness ?? 0,
      contour: this.pending.contour ?? null,
    };
  }

  private emitChange(): void {
    this.dispatchEvent(
      makeEvent<SargassoChangeDetail>(SARGASSO_CHANGE_EVENT, { options: this.readOptions() }),
    );
  }
}
